/**
 * The visitor's chat session: the JWT the legacy API issues after a Facebook or
 * Google sign-in. It is kept in localStorage — the legacy API reads it from an
 * `Authorization` header, not a cookie — and dropped as soon as it expires or
 * the API rejects it, so an expired token is never restored on reload.
 */

const STORAGE_KEY = 'vv.chatToken';

/** Seconds of margin, so a token about to expire is not used for a request. */
const EXPIRY_MARGIN_S = 60;

/** The JWT's `exp` claim, in seconds since the epoch; undefined if unreadable. */
export function tokenExpiry(token: string): number | undefined {
  const payload = token.split('.')[1];

  if (payload === undefined) {
    return undefined;
  }

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);
    const exp =
      typeof claims === 'object' && claims !== null && 'exp' in claims ? claims.exp : undefined;

    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
}

export function isTokenValid(token: string, nowMs: number = Date.now()): boolean {
  const exp = tokenExpiry(token);

  return exp !== undefined && exp - EXPIRY_MARGIN_S > nowMs / 1000;
}

/** Storage throws in some private-browsing modes; a session then lasts the page. */
export function readSession(): string | undefined {
  let token: string | null = null;

  try {
    token = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }

  if (token === null || !isTokenValid(token)) {
    clearSession();

    return undefined;
  }

  return token;
}

export function writeSession(token: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Not persisted; the in-memory session still works for this page.
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}
